import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import './CreatorApplicationReferralInfo.scss';

import { updateCreatorApplication, applyToCreatorWaitlist } from '../../Actions/ApplicationActions';
import { TERMS_AND_CONDITIONS_URL, PRIVACY_POLICY_URL } from '../../Helpers/constants';

import ScrollToTop from '../../Components/General/ScrollToTop';
import Tooltip from '../../Components/General/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCheck } from '@fortawesome/pro-solid-svg-icons';

const CreatorApplicationReferralInfo = props => {
  const application = props.application.creatorApplication;

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      name: 'howTheyHeard',
      label: 'How did you hear about ShopMy?',
      value: application.howTheyHeard || '',
      isFieldValid: () => true,
      isValid: true,
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-textarea-container'>
            <div className='creator-application-input-label'>How did you hear about ShopMy?</div>

            <textarea
              className={cn('creator-application-input', { invalid: !field.isValid, filled: !!field.value })}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { value }));
                props.updateCreatorApplication({ howTheyHeard: value });
              }}
              placeholder='Answer here...'
              rows={3}
            />
          </div>
        );
      }
    },
    {
      name: 'pitch',
      label: 'Tell us about you and your content',
      value: application.pitch || '',
      isFieldValid: () => true,
      isValid: true,
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-textarea-container'>
            <div className='creator-application-input-label'>Tell us about you and your content</div>

            <textarea
              className={cn('creator-application-input', { invalid: !field.isValid, filled: !!field.value })}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { value }));
                props.updateCreatorApplication({ pitch: value });
              }}
              placeholder='Answer here...'
              rows={5}
            />
          </div>
        );
      }
    },
    {
      name: 'hasPublicSocialMedia',
      label: 'Is your social media public?*',
      value: application.hasPublicSocialMedia || '',
      isFieldValid: value => !!value,
      isValid: true,
      errorMessage: 'Please indicate if your social media is public or not',
      customComponent: (field, index) => {
        return (
          <div className='creator-application-input-select-container'>
            <div className='creator-application-input-icon'>
              <FontAwesomeIcon icon={faAngleDown} />
            </div>

            <select
              className={cn('creator-application-input', { invalid: !field.isValid })}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                setFields(prev => updateField(prev, index, { value }));
                props.updateCreatorApplication({ hasPublicSocialMedia: value });
              }}
            >
              <option value=''>Is your social media public?*</option>
              <option value='Yes'>Yes</option>
              <option value='No'>No</option>
            </select>
          </div>
        );
      }
    }
  ]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const validateFields = () => {
    let firstErrorMessage = null;
    const newFields = fields.map(field => {
      const isValidField = field.isFieldValid(field.value);
      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });
    setFields(newFields);

    return firstErrorMessage;
  };

  const goToNextTab = async () => {
    const errorMessage = validateFields();
    if (errorMessage) {
      window.__ADD_EVENT__('Creator Application - Go To Next Tab Error', { tab: 'Referral Info', error: errorMessage });
      return window.ALERT.error(errorMessage);
    } else if (!termsAccepted) {
      window.__ADD_EVENT__('Creator Application - Go To Next Tab Error', { tab: 'Referral Info', error: 'Terms not accepted' });
      return window.ALERT.error('Please accept the terms and conditions');
    }

    const applicationResponse = await props.applyToCreatorWaitlist();
    if (applicationResponse.success) {
      window.__ADD_EVENT__('Creator Application - Application Submitted', { email: application.email });
      props.goToNextTab();
    }
  };

  useEffect(() => {
    Mousetrap.bind('enter', e => {
      e.preventDefault();
      goToNextTab();
    });

    return () => {
      Mousetrap.unbind('enter');
    };
  }, []);

  useEffect(() => {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field.isValid) continue;

      const isValidField = field.isFieldValid(field.value);
      if (isValidField) {
        setFields(fields => {
          const newFields = [...fields];
          newFields[i].isValid = true;
          return newFields;
        });
      }
    }
  }, [fields]);

  return (
    <div className='creator-application-referral-info-outer'>
      <ScrollToTop />
      <div className='creator-application-referral-info-inner'>
        <div className='creator-application-progress-container'>
          <div className='creator-application-progress-container-circle complete clickable' onClick={() => props.goToPreviousTab()}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className='creator-application-progress-container-circle complete clickable' onClick={() => props.goToPreviousTab()}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className='creator-application-progress-container-circle active'>3</div>
        </div>

        <h1>Tell us about you</h1>
        <h2>Getting to know you helps us understand your fit to ShopMy better.</h2>

        <div className='creator-application-referral-info-form'>
          {fields.map((field, index) => (
            <div key={index} className='creator-application-input-container'>
              <Tooltip message={field.isValid ? '' : field.errorMessage}>
                {field.customComponent ? (
                  field.customComponent(field, index)
                ) : (
                  <input
                    className={cn('creator-application-input', {
                      invalid: !field.isValid,
                      alignRight: field.inputValueAlignRight,
                      filled: !!field.value
                    })} // With this we can delay when we show to the errored state
                    type='text'
                    placeholder={field.label}
                    id={field.name}
                    value={field.value}
                    onChange={e => {
                      const { value } = e.target;
                      setFields(prev => updateField(prev, index, { value }));
                      props.updateCreatorApplication({ [field.name]: value });
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        goToNextTab();
                      }

                      if (e.key === 'Escape') {
                        e.preventDefault();
                        const newFields = [...fields];
                        newFields[index].value = '';
                        setFields(newFields);
                        props.updateCreatorApplication({ [field.name]: '' });
                      }
                    }}
                  />
                )}
              </Tooltip>

              {!!field.supportText && (
                <div className={cn('creator-application-input-support-text', { alignRight: !field.inputValueAlignRight })}>{field.supportText}</div>
              )}
            </div>
          ))}

          <div className='creator-application-approvals'>
            <div className='creator-application-checkbox-container'>
              <input
                type='checkbox'
                className='creator-application-checkbox'
                id='creator-application-checkbox'
                value={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
              />
              <label htmlFor='creator-application-checkbox' className='creator-application-checkbox-label'>
                Check this box to consent to ShopMy's{' '}
                <a href={TERMS_AND_CONDITIONS_URL} target='_blank' rel='noopener noreferrer' className='creator-application-link'>
                  Terms and Conditions
                </a>{' '}
                ,{' '}
                <a href={PRIVACY_POLICY_URL} target='_blank' rel='noopener noreferrer' className='creator-application-link'>
                  Privacy Policy
                </a>{' '}
                and to receive marketing emails from ShopMy.
              </label>
            </div>
          </div>

          <div className='creator-application-progress-button-container'>
            <button className='creator-application-progress-button' onClick={() => props.goToPreviousTab()}>
              Back
            </button>
            <button className='creator-application-progress-button primary' onClick={goToNextTab}>
              Next
            </button>
          </div>

          <a href='https://shopmy.us/home/brands' className='creator-application-progress-link'>
            I am a Brand
          </a>
        </div>
      </div>
    </div>
  );
};

CreatorApplicationReferralInfo.propTypes = {
  // redux
  application: PropTypes.object.isRequired,
  updateCreatorApplication: PropTypes.func.isRequired,
  applyToCreatorWaitlist: PropTypes.func.isRequired,

  goToNextTab: PropTypes.func.isRequired,
  goToPreviousTab: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  application: state.application
});

export default connect(mapStateToProps, {
  updateCreatorApplication,
  applyToCreatorWaitlist
})(CreatorApplicationReferralInfo);
